import { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import './TabPanel.css';

/**
 * Two flags for the enclosing tab panel (Asana 1219915395102217), on two clocks.
 *
 * active: this tab is the one the route names. It moves urgently, with the URL,
 * so the hidden tab's URL state freezes (useCardFilterState) and its keyboard
 * handlers stop in the same render the URL changes; there is never a render
 * where the outgoing panel reads the incoming tab's params.
 *
 * visible: this panel is the one shown. It follows the deferred panel index,
 * so hidden plus inert, the scroll remember and restore, and the Add box
 * refocus all wait for the panel to actually be displayed.
 *
 * Both default to true, so a component rendered outside any tab panel behaves
 * as it always has.
 */
interface TabPanelFlags {
  active: boolean;
  visible: boolean;
}
const TabPanelContext = createContext<TabPanelFlags>({ active: true, visible: true });
export const TabPanelActiveProvider = TabPanelContext.Provider;
export const useTabPanelActive = (): boolean => useContext(TabPanelContext).active;
export const useTabPanelVisible = (): boolean => useContext(TabPanelContext).visible;

interface Props {
  children?: React.ReactNode;
  index: number;
  /** The route's tab index (urgent). */
  activeIndex: number;
  /** The panel index being shown (deferred). */
  shownIndex: number;
}

/**
 * The nearest ancestor that scrolls vertically: .main-page-content on desktop
 * (Main.css, overflow-y auto), the window when nothing between here and the
 * body scrolls (the mobile shell).
 */
export const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * A visited tab stays mounted and is hidden with the hidden attribute plus
 * display none, and made inert so it never takes focus or keyboard input.
 * display none discards the scroller's offset (Chromium clamps the shared
 * scroller when the hidden subtree shortens the page), so the offset is
 * remembered while the panel is visible and put back after it shows again.
 */
export const TabPanel = ({ children, index, activeIndex, shownIndex }: Props) => {
  const active = activeIndex === index;
  const visible = shownIndex === index;
  const ref = useRef<HTMLDivElement>(null);
  const savedScroll = useRef(0);
  const flagsRef = useRef<TabPanelFlags>({ active, visible });
  if (flagsRef.current.active !== active || flagsRef.current.visible !== visible) {
    flagsRef.current = { active, visible };
  }

  useEffect(() => {
    if (!visible) return undefined;
    const scroller = findScrollContainer(ref.current);
    const target: HTMLElement | Window = scroller ?? window;
    const remember = () => {
      savedScroll.current = scroller ? scroller.scrollTop : window.scrollY;
    };
    target.addEventListener('scroll', remember, { passive: true });
    return () => target.removeEventListener('scroll', remember);
  }, [visible]);

  useLayoutEffect(() => {
    if (!visible) return;
    const scroller = findScrollContainer(ref.current);
    if (scroller) scroller.scrollTop = savedScroll.current;
    else window.scrollTo(0, savedScroll.current);
  }, [visible]);

  return (
    <TabPanelActiveProvider value={flagsRef.current}>
      <div
        ref={ref}
        role="tabpanel"
        hidden={!visible}
        inert={!visible}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        className="tab-panel"
        data-testid={`tab-panel-${index}`}
      >
        {children}
      </div>
    </TabPanelActiveProvider>
  );
};
